import { useEffect, useState } from "react";
import { useOfflineQueue } from "../../state/offlineQueue";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { getDemoScans } from "../../services/demoService";
import { MapPin, Clock } from "lucide-react";

type LocationData = {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
} | null;

export function HistoryPage() {
  const { drafts, load, sync, syncing } = useOfflineQueue();
  const online = useOnlineStatus();
  const [currentLocation, setCurrentLocation] = useState<LocationData>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [demoScans, setDemoScans] = useState(getDemoScans());

  useEffect(() => {
    void load();
    // Refresh demo scans periodically
    const interval = setInterval(() => {
      setDemoScans(getDemoScans());
    }, 5000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (online) {
      void sync();
    }
  }, [online, sync]);

  useEffect(() => {
    // Try to get current location on mount
    getCurrentLocation();
  }, []);

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser");
      return;
    }

    setLocationError("Getting precise GPS location...");

    try {
      console.log("Getting optimized GPS reading...");

      // Strategy 1: Try watchPosition for better accuracy (stops automatically)
      const getOptimizedPosition = (): Promise<GeolocationPosition> => {
        return new Promise((resolve, reject) => {
          let bestPosition: GeolocationPosition | null = null;
          let attempts = 0;
          const maxAttempts = 3;

          const watchId = navigator.geolocation.watchPosition(
            (position) => {
              attempts++;
              console.log(`GPS attempt ${attempts}: ±${Math.round(position.coords.accuracy)}m (age: ${Date.now() - position.timestamp}ms)`);

              // Accept position if it's accurate enough or we've tried enough times
              if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy) {
                bestPosition = position;
              }

              // Stop watching if we get good accuracy or max attempts
              if (position.coords.accuracy <= 20 || attempts >= maxAttempts) {
                navigator.geolocation.clearWatch(watchId);
                resolve(bestPosition);
              }
            },
            (error) => {
              navigator.geolocation.clearWatch(watchId);
              reject(error);
            },
            {
              enableHighAccuracy: true,
              timeout: 8000, // Shorter timeout for faster response
              maximumAge: 0 // Force fresh reading - no cached positions
            }
          );

          // Fallback timeout to prevent hanging
          setTimeout(() => {
            navigator.geolocation.clearWatch(watchId);
            if (bestPosition) {
              resolve(bestPosition);
            } else {
              reject(new Error("GPS timeout - no position obtained"));
            }
          }, 10000);
        });
      };

      const position = await getOptimizedPosition();

      // Use actual accuracy - no artificial improvements
      setCurrentLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      });
      setLocationError(null);

      console.log(`GPS complete: ±${Math.round(position.coords.accuracy)}m accuracy`);

    } catch (error: any) {
      // Fallback to basic getCurrentPosition if watchPosition fails
      console.warn("Optimized GPS failed, falling back to basic GPS:", error);
      try {
        const fallbackPosition = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 60000 // Allow cached position for speed
            }
          );
        });

        setCurrentLocation({
          latitude: fallbackPosition.coords.latitude,
          longitude: fallbackPosition.coords.longitude,
          accuracy: fallbackPosition.coords.accuracy,
          timestamp: fallbackPosition.timestamp
        });
        setLocationError(null);
        console.log(`Fallback GPS: ±${Math.round(fallbackPosition.coords.accuracy)}m accuracy`);

      } catch (fallbackError: any) {
        setLocationError(`Location error: ${fallbackError.message}`);
      }
    }
  };

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-800">Scan History</h1>
        <p className="text-sm text-gray-600">Today&apos;s sync status across verified checkpoints.</p>
      </header>

      {/* Pending Offline Scans */}
      {drafts.length > 0 && (
        <>
          <h2 className="text-lg font-medium text-gray-800">Pending Sync</h2>
          <div className="space-y-3">
            {drafts.map((draft) => (
              <article key={draft.id} className="rounded-lg border border-orange-200 bg-orange-50 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Checkpoint token</p>
                    <p className="text-xs text-gray-600">{draft.token}</p>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <p>Queued {new Date(draft.createdAt).toLocaleTimeString()}</p>
                    <p>Retries: {draft.retries}</p>
                    <p>Online at capture: {draft.online ? "Yes" : "No"}</p>
                  </div>
                </div>
                <div className="mt-2 grid gap-1 text-xs text-gray-600">
                  <p>Location: {draft.lat?.toFixed(5) ?? "—"}, {draft.lng?.toFixed(5) ?? "—"}</p>
                  <p>Accuracy: {draft.accuracy ? `${Math.round(draft.accuracy)}m` : "—"}</p>
                  {draft.notes && <p>Note: {draft.notes}</p>}
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {/* Demo Scans History */}
      <h2 className="text-lg font-medium text-gray-800">Recent Scans</h2>
      <div className="space-y-3">
        {demoScans.length === 0 ? (
          <p className="rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-600">
            No scans recorded yet. Start scanning QR codes to see your patrol history.
          </p>
        ) : (
          demoScans.slice(0, 10).map((scan) => (
            <article key={scan.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">{scan.checkpointName}</p>
                  <p className="text-xs text-gray-600">{scan.qrToken}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${scan.status === 'on-time' ? 'bg-green-100 text-green-800' :
                        scan.status === 'early' ? 'bg-blue-100 text-blue-800' :
                          scan.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                      }`}>
                      {scan.status.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Clock className="w-3 h-3" />
                    {new Date(scan.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <div className="mt-2 grid gap-1 text-xs text-gray-600">
                <p>Location: {scan.lat?.toFixed(5) ?? "—"}, {scan.lng?.toFixed(5) ?? "—"}</p>
                <p>Accuracy: {scan.accuracy ? `±${Math.round(scan.accuracy)}m` : "—"}</p>
                {scan.notes && <p>Note: {scan.notes}</p>}
                <p>Site: {scan.siteName}</p>
              </div>
            </article>
          ))
        )}
      </div>

      {syncing && (
        <p className="text-xs text-gray-600">Attempting to sync queued scans…</p>
      )}

      {/* GPS Location Section */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-medium text-gray-800">Current Location</h2>
        </div>

        {currentLocation ? (
          <div className="space-y-3">
            <div className="text-sm space-y-1">
              <p className="text-gray-600">
                <span className="font-medium">Latitude:</span> {currentLocation.latitude.toFixed(6)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Longitude:</span> {currentLocation.longitude.toFixed(6)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Accuracy:</span> ±{Math.round(currentLocation.accuracy)}m
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Last Updated:</span> {new Date(currentLocation.timestamp).toLocaleTimeString()}
              </p>
            </div>

            {/* Map iframe */}
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <iframe
                width="100%"
                height="200"
                frameBorder="0"
                scrolling="no"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentLocation.longitude - 0.005},${currentLocation.latitude - 0.005},${currentLocation.longitude + 0.005},${currentLocation.latitude + 0.005}&layer=mapnik&marker=${currentLocation.latitude},${currentLocation.longitude}`}
                style={{ border: 0 }}
              />
            </div>

            <button
              onClick={getCurrentLocation}
              className="text-sm px-3 py-1 rounded-md border transition-colors"
              style={{
                borderColor: '#D1C4B7',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9F7F5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Update Location
            </button>
          </div>
        ) : locationError ? (
          <p className="text-sm text-red-600">{locationError}</p>
        ) : (
          <p className="text-sm text-gray-600">Fetching location...</p>
        )}
      </div>
    </section>
  );
}
